import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MediaItemData, MediaItem } from '@/core/MediaItem/MediaItem.model';
import { MediaStage } from '@/core/types/mediaTypes';
import {
  ScrollableContainer,
  MediaGridMobile,
} from '@/components/MediaContainer';
import { useRole } from '@/contexts/RoleContext';
import { UnifiedAuthenticatedImage } from '@/components/ui/UnifiedAuthenticatedImage';
import { MediaDisplay } from '@/components/MediaCard';
import { cn } from '@/utils/cn';
import { StarRating } from '@/components/ui/StarRating';
import { RatingBadge } from '@/components/MediaCard/MediaBadges';
import { DownloadQualityModal } from '@/components/ui/DownloadQualityModal';
import { ReviewPager } from '@/components/ui/ReviewPager';
import { ScrollMoreHint } from '@/components/ui/ScrollMoreHint';
import { ImageIcon } from '@/components/icons';
import { useReviewUrlState } from '@/core/navigation/useReviewUrlState';

// In the all-photos feed (which can be large), pull the next page once the
// client is within this many items of the end.
const FEED_PREFETCH = 3;

interface ApprovalMediaListProps {
  mediaItems: MediaItemData[];
  clientId: string;
  token?: string;
  currentStage?: string;
  contentLabel?: string;
  onStageChange: (stage?: string) => void;
  onRate: (mediaId: number, rating?: number, comment?: string, skipRefetch?: boolean) => Promise<void>;
  onUpdateLabel: (mediaId: number, label: string) => Promise<void>;
  onLoadMore?: () => Promise<void>;
  hasMore?: boolean;
  totalCount?: number;
}

export const ApprovalMediaList: React.FC<ApprovalMediaListProps> = ({
  mediaItems,
  clientId,
  token,
  currentStage,
  contentLabel = 'foto',
  onRate,
  onLoadMore,
  hasMore = false,
  totalCount = 0,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const review = useReviewUrlState();
  const { open: openReview, initialMediaId, setCurrentItem } = review;

  const isPending = currentStage === MediaStage.PENDING_APPROVAL;
  const filteredMediaItems = mediaItems;
  // View mode is decoupled from the filter: the feed shows when a photo is open
  // (reviewItem in the URL), otherwise the grid overview. Both the pending and
  // the all-photos filters get both modes.
  const showFeed = review.isOpen;

  const safeIndex = Math.min(currentIndex, filteredMediaItems.length - 1);

  // Reset to the first item only when the underlying data set changes (stage or
  // content type) — not on length, so appended pages never bounce the position.
  useEffect(() => {
    setCurrentIndex(0);
  }, [currentStage, contentLabel]);

  // Seed the feed at the tapped photo. Seed once per open (tracked by the ref)
  // so a later page load can still land it, but an optimistic rating patch —
  // which rebuilds the array — never yanks the client back to the opened photo.
  const seededForRef = useRef<number | 'start' | null>(null);
  useEffect(() => {
    if (!showFeed) {
      seededForRef.current = null;
      return;
    }
    const target = initialMediaId ?? 'start';
    if (seededForRef.current === target) return;
    if (target === 'start') {
      setCurrentIndex(0);
      seededForRef.current = 'start';
      return;
    }
    const idx = filteredMediaItems.findIndex((m) => m.id === target);
    if (idx >= 0) {
      setCurrentIndex(idx);
      seededForRef.current = target;
    }
  }, [showFeed, initialMediaId, filteredMediaItems]);

  const loadingMoreRef = useRef(false);
  // Pending queue is small: eagerly pull every page so the grid overview and the
  // feed both show the whole queue (server paginates at per_page=50).
  useEffect(() => {
    if (!isPending || !hasMore || !onLoadMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    void onLoadMore().finally(() => {
      loadingMoreRef.current = false;
    });
  }, [isPending, hasMore, onLoadMore, filteredMediaItems.length]);

  // All-photos can be large: in the feed, pull the next page as the client nears
  // the end instead of loading everything. The grid keeps its manual Load more.
  useEffect(() => {
    if (isPending || !showFeed || !hasMore || !onLoadMore || loadingMoreRef.current) return;
    if (safeIndex < filteredMediaItems.length - FEED_PREFETCH) return;
    loadingMoreRef.current = true;
    void onLoadMore().finally(() => {
      loadingMoreRef.current = false;
    });
  }, [isPending, showFeed, hasMore, onLoadMore, safeIndex, filteredMediaItems.length]);

  // Move within the feed and persist the position in the URL so a reload
  // restores it. Written imperatively here — NOT in a render effect — because
  // setSearchParams has an unstable identity, so an effect that depends on it
  // re-fires every render and loops forever (max update depth).
  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, filteredMediaItems.length - 1));
      setCurrentIndex(clamped);
      const id = filteredMediaItems[clamped]?.id;
      if (id != null) setCurrentItem(id);
    },
    [filteredMediaItems, setCurrentItem],
  );

  const handleNext = useCallback(() => {
    goTo(currentIndex + 1);
  }, [goTo, currentIndex]);

  const handlePrevious = useCallback(() => {
    goTo(currentIndex - 1);
  }, [goTo, currentIndex]);

  // Arrow keys drive the feed only.
  useEffect(() => {
    if (!showFeed) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFeed, handleNext, handlePrevious]);

  if (filteredMediaItems.length === 0) {
    const labelCapitalized = contentLabel.charAt(0).toUpperCase() + contentLabel.slice(1);
    return (
      <div className="text-center py-12">
        <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600 font-medium">
          {currentStage === MediaStage.PENDING_APPROVAL
            ? `Nessun ${contentLabel} da valutare`
            : `Nessun ${contentLabel} disponibile`}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          {currentStage === MediaStage.PENDING_APPROVAL
            ? `${labelCapitalized === 'Foto' ? 'Tutte le foto sono state revisionate' : `Tutti i ${contentLabel} sono stati revisionati`}`
            : `La libreria ${contentLabel} e vuota`}
        </p>
      </div>
    );
  }

  // safeIndex is clamped above so a stale high index can't read past the array.
  const currentMedia = filteredMediaItems[safeIndex];
  if (!currentMedia) return null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      {!showFeed ? (
        <MediaGridView
          mediaItems={filteredMediaItems}
          clientId={clientId}
          token={token}
          onOpenItem={openReview}
          onLoadMore={isPending ? undefined : onLoadMore}
          hasMore={isPending ? false : hasMore}
          totalCount={totalCount}
        />
      ) : (
        <>
          {/* Scroll region — owns vertical overflow so the pager bar
              below it sits naturally above any tap targets. */}
          <div ref={scrollRef} className="relative flex-1 min-h-0 overflow-y-auto">
            <div className="mx-auto flex w-full max-w-2xl items-start justify-center px-4 lg:px-6">
              <div className="w-full">
                <MediaApprovalCard
                  key={currentMedia.id}
                  media={currentMedia}
                  clientId={clientId}
                  token={token}
                  contentLabel={contentLabel}
                  onRate={onRate}
                  allowRating={isPending}
                  totalCount={filteredMediaItems.length}
                  currentIndex={safeIndex}
                />
              </div>
            </div>
            <ScrollMoreHint
              scrollRef={scrollRef}
              storageKey={`media-review-${contentLabel}`}
              label={isPending ? 'Scorri per votare' : 'Scorri per vedere'}
            />
          </div>

          <ReviewPager
            currentIndex={safeIndex}
            total={filteredMediaItems.length}
            onPrev={handlePrevious}
            onNext={handleNext}
            reviewedFlags={filteredMediaItems.map((m) => Boolean(m.clientReviewedAt))}
          />
        </>
      )}
    </div>
  );
};

interface MediaApprovalCardProps {
  media: MediaItemData;
  clientId: string;
  token?: string;
  contentLabel?: string;
  onRate: (mediaId: number, rating?: number, comment?: string, skipRefetch?: boolean) => Promise<void>;
  /** Whether the rating controls (stars + comment) are shown. Only the pending
   * review flow rates; the all-photos gallery is view-only. */
  allowRating?: boolean;
  totalCount: number;
  currentIndex: number;
}

const MediaApprovalCard: React.FC<MediaApprovalCardProps> = ({
  media,
  clientId,
  token,
  contentLabel = 'foto',
  onRate,
  allowRating = true,
  totalCount,
  currentIndex,
}) => {
  const { role, clientId: contextClientId, token: contextToken } = useRole();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [rating, setRating] = React.useState(media.clientRating ?? 0);
  const [comment, setComment] = React.useState(media.clientComment || '');
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const commentTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveClientId = contextClientId || clientId;
  const effectiveToken = contextToken || token;

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (commentTimerRef.current) clearTimeout(commentTimerRef.current);
    };
  }, []);

  // Star tap: save the rating with the current comment and stay put.
  // Navigation is deliberate — the client advances with the pager arrows,
  // so a rating never yanks them off the current photo.
  const handleStarTap = async (newRating: number) => {
    if (isSubmitting) return;
    setRating(newRating);
    setIsSubmitting(true);
    // Flush any pending comment debounce
    if (commentTimerRef.current) {
      clearTimeout(commentTimerRef.current);
      commentTimerRef.current = null;
    }
    try {
      // skipRefetch: the hook patches local state optimistically. A page-1
      // refetch here would drop the eagerly-loaded later pages and reset the
      // carousel position.
      await onRate(media.id, newRating, comment || undefined, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Comment change: debounce auto-save (skip list refetch to avoid resetting carousel)
  const handleCommentChange = (value: string) => {
    setComment(value);
    if (commentTimerRef.current) clearTimeout(commentTimerRef.current);
    commentTimerRef.current = setTimeout(async () => {
      await onRate(media.id, rating > 0 ? rating : undefined, value || undefined, true);
    }, 800);
  };

  const mediaItem = new MediaItem(media, role, effectiveClientId, effectiveToken);

  return (
    <div className="flex flex-col">
      {/* Media Display */}
      <div className={cn(
        "relative w-full bg-gray-100 group/image",
        contentLabel === 'reel' ? "aspect-9/16" : "aspect-4/5"
      )}>
        <MediaDisplay
          mediaItem={mediaItem}
          autoPlay={contentLabel === 'reel'}
          className="w-full h-full"
        />

        {/* Download button */}
        <div className="absolute top-2 right-2 z-20 sm:opacity-0 sm:group-hover/image:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDownloadModal(true);
            }}
            className="bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-full shadow-xs transition-all hover:shadow-md"
            title="Download file"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Rating controls — only in the pending review flow. The all-photos
          gallery is view-only, so a client can never change a review's stars
          or comment from there. */}
      {allowRating && (
        <div className="bg-white border-t border-gray-200">
          <div className="flex justify-center pt-4 pb-3">
            <StarRating
              value={rating}
              onChange={handleStarTap}
              disabled={isSubmitting}
              size="lg"
            />
          </div>

          <div className="px-4 pb-3">
            <textarea
              id={`comment-${media.id}`}
              value={comment}
              onChange={(e) => handleCommentChange(e.target.value)}
              placeholder="Aggiungi un commento (opzionale)"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
              disabled={isSubmitting}
            />
          </div>
        </div>
      )}

      <DownloadQualityModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        mediaData={media}
      />
    </div>
  );
};

interface MediaGridViewProps {
  mediaItems: MediaItemData[];
  clientId: string;
  token?: string;
  /** Open the full-screen review feed at this item. */
  onOpenItem: (mediaId: number) => void;
  onLoadMore?: () => Promise<void>;
  hasMore?: boolean;
  totalCount?: number;
}

const MediaGridView: React.FC<MediaGridViewProps> = ({
  mediaItems,
  clientId,
  token,
  onOpenItem,
  onLoadMore,
  hasMore = false,
  totalCount = 0,
}) => {
  const { role, clientId: contextClientId, token: contextToken } = useRole();
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [selectedMedia, setSelectedMedia] = React.useState<MediaItemData | null>(null);

  const effectiveClientId = contextClientId || clientId;
  const effectiveToken = contextToken || token;

  const handleLoadMore = async () => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderMediaCard = (media: MediaItemData) => {
    const mediaItem = new MediaItem(media, role, effectiveClientId, effectiveToken);
    const hasRating = media.clientRating != null;

    return (
      <div
        key={media.id}
        role="button"
        tabIndex={0}
        onClick={() => onOpenItem(media.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenItem(media.id);
          }
        }}
        className="relative aspect-4/5 bg-gray-100 group/grid-item overflow-hidden rounded-xs cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <UnifiedAuthenticatedImage
          mediaItem={mediaItem}
          alt={media.originalFilename || 'Media item'}
          className="w-full h-full object-cover"
        />

        {/* Download button */}
        <div className="absolute top-1.5 right-1.5 z-20 sm:opacity-0 sm:group-hover/grid-item:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMedia(media);
            }}
            className="bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-full shadow-xs transition-all hover:shadow-md"
            title="Download file"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        </div>

        {/* Star rating badge - bottom right */}
        {hasRating && (
          <div className="absolute bottom-2 right-2 z-20">
            <RatingBadge rating={media.clientRating!} />
          </div>
        )}

        {/* Comment/label overlay on hover */}
        {(media.clientComment || media.label) && (
          <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent p-2 opacity-0 group-hover/grid-item:opacity-100 transition-opacity">
            <p className="text-white text-xs truncate">{media.clientComment || media.label}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ScrollableContainer
        height="calc(100vh - 120px)"
        padding="none"
        className="bg-gray-50 border-0 shadow-none rounded-none"
      >
        {/* Grid rides the shared portal column so it aligns with the
            header gutters (DD-025). */}
        <div className="w-full max-w-2xl mx-auto px-4 lg:px-6">
          {/* Desktop/Tablet grid — fixed 3-up, IG-style */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-3 gap-4">
              {mediaItems.map(renderMediaCard)}
            </div>
          </div>

          {/* Mobile grid */}
          <div className="block sm:hidden">
            <MediaGridMobile>
              {mediaItems.map(renderMediaCard)}
            </MediaGridMobile>
          </div>

          {hasMore && (
            <div className="flex flex-col items-center gap-2 py-6 mt-4">
              <p className="text-sm text-gray-600">
                Showing {mediaItems.length} of {totalCount} items
              </p>
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </ScrollableContainer>

      {selectedMedia && (
        <DownloadQualityModal
          isOpen={!!selectedMedia}
          onClose={() => setSelectedMedia(null)}
          mediaData={selectedMedia}
        />
      )}
    </>
  );
};
